import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { ShieldCheck, AlertTriangle, Search, RefreshCw, Download } from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "auth", label: "Auth" },
  { value: "phi", label: "PHI access" },
  { value: "admin", label: "Admin actions" },
];

const STATUS_STYLES = {
  success: "bg-[#E7EEE3] text-[#3F6B45]",
  failure: "bg-[#F1E0DD] text-[#8F3A3A]",
  blocked: "bg-[#FBEFD6] text-[#8a6620]",
};

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function AdminAudit() {
  const [data, setData] = useState(null);
  const [retention, setRetention] = useState(null);
  const [filters, setFilters] = useState({ category: "", action: "", actor_email: "", status: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      params.append("limit", "200");
      const [logs, ret] = await Promise.all([
        api.get(`/admin/audit-logs?${params.toString()}`),
        api.get("/admin/audit-retention"),
      ]);
      setData(logs.data);
      setRetention(ret.data);
    } catch (_) { console.debug("Audit load failed", _); setData({ items: [], total: 0 }); } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      ["category", "action", "actor_email", "status"].forEach((k) => { if (filters[k]) params.append(k, filters[k]); });
      const res = await api.get(`/admin/audit-logs/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `badi-audit-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_) { console.error("CSV export failed:", _); }
  };

  return (
    <AdminLayout>
      <p className="label-eyebrow mb-2">Admin · Audit trail</p>
      <h1 className="font-serif text-4xl text-[#2D322B] leading-tight mb-2">Who touched what, and when.</h1>
      <p className="text-[#5C6359] mb-6 text-sm max-w-2xl flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#3F6B45]" />
        Append-only record of auth, PHI access, and admin actions. Health records are retained for
        {retention ? ` ${retention.retention_years}` : ""} years.
      </p>

      {retention?.warning && (
        <div className="mb-6 flex items-start gap-3 bg-[#FBEFD6] border border-[#E3C77E] rounded-xl p-4" data-testid="retention-warning">
          <AlertTriangle className="w-5 h-5 text-[#8a6620] shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-[#6b4f12]">Retention expiry approaching</p>
            <p className="text-sm text-[#8a6620] mt-1">
              {retention.expiring_soon_count} record(s) are within {retention.warn_window_days} days of their
              {` ${retention.retention_years}`}-year retention expiry. Review before they are purged.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-5" data-testid="audit-filters">
        <select
          value={filters.category}
          onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
          data-testid="audit-filter-category"
          className="bg-white border border-[#E8DFD0] rounded px-3 py-2 text-sm text-[#2D322B]"
        >
          {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input
          value={filters.action}
          onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
          placeholder="action (e.g. auth.login)"
          data-testid="audit-filter-action"
          className="bg-white border border-[#E8DFD0] rounded px-3 py-2 text-sm"
        />
        <input
          value={filters.actor_email}
          onChange={(e) => setFilters((f) => ({ ...f, actor_email: e.target.value }))}
          placeholder="actor email"
          data-testid="audit-filter-email"
          className="bg-white border border-[#E8DFD0] rounded px-3 py-2 text-sm"
        />
        <select
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          data-testid="audit-filter-status"
          className="bg-white border border-[#E8DFD0] rounded px-3 py-2 text-sm text-[#2D322B]"
        >
          <option value="">Any status</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
          <option value="blocked">Blocked</option>
        </select>
        <button onClick={load} data-testid="audit-refresh" className="btn-primary !py-2 inline-flex items-center justify-center gap-2">
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
        </button>
      </div>

      <p className="text-xs text-[#9A8F7E] mb-3" data-testid="audit-count">
        {data ? `${data.items.length} of ${data.total} events` : "Loading…"}
      </p>

      <div className="flex justify-end mb-3">
        <button onClick={exportCsv} data-testid="audit-export-csv" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#3F6B45] hover:text-[#2D322B] border border-[#BBD0B7] rounded-full px-4 py-2">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      <div className="bg-white border border-[#E8DFD0] rounded-lg overflow-hidden" data-testid="audit-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#9A8F7E] border-b border-[#E8DFD0] uppercase tracking-widest text-[11px]">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Actor</th>
                <th className="px-4 py-3">Resource</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {data?.items?.map((e) => (
                <tr key={e.id} className="border-b border-[#F1ECE4] hover:bg-[#FAF7F0]" data-testid={`audit-row-${e.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-[#5C6359]">{fmt(e.ts)}</td>
                  <td className="px-4 py-3 capitalize text-[#5C6359]">{e.category}</td>
                  <td className="px-4 py-3 font-mono text-[12px] text-[#2D322B]">{e.action}</td>
                  <td className="px-4 py-3 text-[#5C6359]">{e.actor_email || e.actor_id || "—"}</td>
                  <td className="px-4 py-3 text-[#5C6359]">{e.resource || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_STYLES[e.status] || "bg-[#EDE7DC] text-[#5C6359]"}`}>
                      {e.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#9A8F7E] font-mono text-[12px]">{e.ip}</td>
                </tr>
              ))}
              {data && data.items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[#9A8F7E]">No matching events.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
